// features/projects/templates/continuum/index.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM WORKSTATION — top-level shell with 4 tabs.
//
// Replaces the standard ProjectWorkstation when a project's
// template === 'continuum'. Wired in from features/projects/index.js.
//
// Tab nav:
//   Dashboard    — Signal-style species/cascade dashboard
//   Chat         — embeds the standard ProjectWorkstation 1:1
//                  (chat | canvas | journey + Excavate)
//   Simulation   — Setup + Findings (cross-system pipeline)
//   Resources    — files / datasets / API keys (stub for now)
//
// Visual tokens: see ./tokens.js. Source of truth is the original
// HTML mockups (continuum_simulation.html, signal_dashboard.html).
// Do NOT normalize colors/fonts to Shift's standard tokens.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import Head from 'next/head';
import { C, FONT } from './tokens';
import ProjectWorkstation from '../../project-workstation';
import ContinuumDashboard from './dashboard';
import ContinuumSimulation from './simulation';
import ContinuumResources from './resources';
import ContinuumFindings from './findings';
import ContinuumRecord from './record';
import ContinuumOutput from './output';
import ContinuumSpeciesDashboard from './species-dashboard';
import ContinuumCascade from './cascade';
import ContinuumSimulate from './simulate';

// Fonts used across the Continuum template (and its Signal sub-tab).
// Loaded once at the shell level so all four tabs render with the
// correct typography from the source mockups.
const FONT_LINK = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Mono:wght@300;400;500&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&display=swap';

const NOOP = () => {};

const TABS = [
  { key: 'dashboard',  label: 'Dashboard',  color: C.gold },
  { key: 'chat',       label: 'Chat',       color: C.sage },
  { key: 'simulation', label: 'Simulation', color: C.teal },
  { key: 'findings',   label: 'Findings',   color: C.purple || C.amber },
  { key: 'cascade',    label: 'Cascade',    color: C.gold },
  { key: 'simulate',   label: 'Simulate',   color: C.sage },
  { key: 'record',     label: 'Record',     color: C.teal },
  { key: 'output',     label: 'Output',     color: C.purple || C.gold },
  { key: 'species',    label: 'Species',    color: C.sage },
  { key: 'resources',  label: 'Resources',  color: C.gold },
];

export default function ContinuumWorkstation({
  userId,
  userRecord,
  supabase,
  workspaceSession,
  voiceConfig,
  onExit,
  onSidebarCollapse,
}) {
  const workspaceId = workspaceSession?.id;
  const projectTitle = workspaceSession?.name || 'The Continuum';

  // Tab persistence — keyed by workspace so each Continuum project
  // remembers its own last-active tab. Starts on Dashboard.
  const storageKey = `continuum_tab_${workspaceId}`;
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'dashboard';
    return window.localStorage.getItem(storageKey) || 'dashboard';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, activeTab);
  }, [storageKey, activeTab]);

  // Collapse the parent sidebar while the Continuum workstation is open
  useEffect(() => {
    onSidebarCollapse?.(true);
    return () => onSidebarCollapse?.(false);
  }, [onSidebarCollapse]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: C.bg,
        color: C.text,
        fontFamily: FONT.sans,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Head>
        <link href={FONT_LINK} rel="stylesheet" />
      </Head>

      {/* ── TOP BAR ───────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          height: 48,
          borderBottom: `1px solid ${C.b1}`,
          background: C.s1,
          flexShrink: 0,
          gap: 14,
        }}
      >
        <div
          style={{
            fontFamily: FONT.serif,
            fontSize: 18,
            fontWeight: 300,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ color: C.gold }}>◈</span>
          <span>{projectTitle}</span>
        </div>
        <div style={{ width: 1, height: 18, background: C.b2 }} />
        <div style={{ fontSize: 10, color: C.static, fontFamily: FONT.mono }}>
          Continuum Template
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 24 }}>
          {TABS.map((t) => {
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  fontSize: 10,
                  fontFamily: FONT.mono,
                  letterSpacing: '0.06em',
                  padding: '6px 14px',
                  borderRadius: 4,
                  border: `1px solid ${isActive ? t.color : 'transparent'}`,
                  background: isActive
                    ? hexToBg(t.color, 0.1)
                    : 'transparent',
                  color: isActive ? t.color : C.static,
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  textTransform: 'uppercase',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = C.mid;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = C.static;
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onExit}
            title="Exit"
            style={{
              background: 'none',
              border: `1px solid ${C.b2}`,
              borderRadius: 4,
              color: C.static,
              fontSize: 10,
              fontFamily: FONT.mono,
              letterSpacing: '0.06em',
              padding: '5px 12px',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = C.terra;
              e.currentTarget.style.borderColor = C.terra;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = C.static;
              e.currentTarget.style.borderColor = C.b2;
            }}
          >
            Exit
          </button>
        </div>
      </div>

      {/* ── TAB CONTENT ───────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        {activeTab === 'dashboard' && (
          <ContinuumDashboard
            workspaceId={workspaceId}
            userId={userId}
            onNavigate={setActiveTab}
          />
        )}

        {activeTab === 'chat' && (
          <ProjectWorkstation
            userId={userId}
            userRecord={userRecord}
            supabase={supabase}
            workspaceSession={workspaceSession}
            voiceConfig={voiceConfig}
            onExit={onExit}
            // Continuum shell owns the sidebar state for its full
            // lifetime — pass a no-op so the embedded workstation
            // can't expand the sidebar on tab-switch unmount.
            onSidebarCollapse={NOOP}
            embedded
          />
        )}

        {activeTab === 'simulation' && (
          <ContinuumSimulation
            workspaceId={workspaceId}
            userId={userId}
          />
        )}

        {activeTab === 'species' && (
          <ContinuumSpeciesDashboard
            workspaceId={workspaceId}
            userId={userId}
          />
        )}

        {activeTab === 'findings' && (
          <ContinuumFindings
            workspaceId={workspaceId}
            userId={userId}
          />
        )}

        {activeTab === 'cascade' && (
          <ContinuumCascade
            workspaceId={workspaceId}
            userId={userId}
          />
        )}

        {activeTab === 'simulate' && (
          <ContinuumSimulate
            workspaceId={workspaceId}
            userId={userId}
          />
        )}

        {activeTab === 'record' && (
          <ContinuumRecord
            workspaceId={workspaceId}
            userId={userId}
          />
        )}

        {activeTab === 'output' && (
          <ContinuumOutput
            workspaceId={workspaceId}
            userId={userId}
          />
        )}

        {activeTab === 'resources' && (
          <ContinuumResources
            workspaceId={workspaceId}
            userId={userId}
          />
        )}
      </div>
    </div>
  );
}

// Convert hex (e.g. '#c8a96e') to an rgba background string with alpha.
function hexToBg(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
